import React from 'react';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';

const TREND_UP = '#4CAF50';
const TREND_DOWN = '#F44336';
const TREND_FLAT = '#FFC107';
const TOLERANCE = 0.10;
const FIELD_MIN_WIDTH = 230;

const METRIC_TOOLTIPS = {
	reviews: 'Total reviews in the last 7, 30, and 90 days. Each value is colored by its daily average compared to the next longer period (7d vs 30d, 30d vs 90d, 90d vs 180d). Green = improving, red = declining, yellow = within 10%.',
	avSales: 'AV keys sold in the last 7, 30, and 90 days. Each value is colored by its daily average compared to the next longer period (7d vs 30d, 30d vs 90d, 90d vs 180d). Green = improving, red = declining, yellow = within 10%.',
};

const perDayRate = (count, days) => (Number(count) || 0) / days;

export function trendColor(shortCount, shortDays, longCount, longDays) {
	const shortRate = perDayRate(shortCount, shortDays);
	const longRate = perDayRate(longCount, longDays);

	if (shortRate === 0 && longRate === 0) {
		return TREND_FLAT;
	}
	if (longRate === 0) {
		return shortRate > 0 ? TREND_UP : TREND_FLAT;
	}

	const pctDiff = (shortRate - longRate) / longRate;
	if (Math.abs(pctDiff) <= TOLERANCE) {
		return TREND_FLAT;
	}
	return pctDiff > 0 ? TREND_UP : TREND_DOWN;
}

const TrendStackedInput = React.forwardRef(function TrendStackedInput({ segments, ...props }, ref) {
	return (
		<div
			ref={ref}
			{...props}
			style={{
				...props.style,
				display: 'flex',
				alignItems: 'center',
				whiteSpace: 'nowrap',
				minWidth: FIELD_MIN_WIDTH - 32,
			}}
		>
			{segments.map((segment, index) => (
				<React.Fragment key={index}>
					{index > 0 && <span>{' / '}</span>}
					<span style={{ color: segment.color, fontWeight: 500 }}>{segment.value ?? 0}</span>
				</React.Fragment>
			))}
		</div>
	);
});

export default function TrendStackedMetric({ label, values, baseline, periods = [7, 30, 90, 180], metric, tooltip }) {
	const [days7, days30, days90, days180] = periods;
	const [count7, count30, count90] = values;
	const count180 = baseline;
	const tooltipTitle = tooltip ?? METRIC_TOOLTIPS[metric] ?? '';

	const segments = [
		{ value: count7, color: trendColor(count7, days7, count30, days30) },
		{ value: count30, color: trendColor(count30, days30, count90, days90) },
		{ value: count90, color: trendColor(count90, days90, count180, days180) },
	];

	const field = (
		<TextField
			fullWidth={true}
			size={"small"}
			label={label}
			variant="outlined"
			disabled={true}
			style={{ minWidth: FIELD_MIN_WIDTH }}
			InputLabelProps={{ shrink: true }}
			InputProps={{
				readOnly: true,
				inputComponent: TrendStackedInput,
				inputProps: { segments },
			}}
			value={" "}
		/>
	);

	if (!tooltipTitle) {
		return field;
	}

	return (
		<Tooltip title={tooltipTitle} arrow>
			<span style={{ display: 'inline-block', minWidth: FIELD_MIN_WIDTH }}>{field}</span>
		</Tooltip>
	);
}
